import { useEffect, useCallback } from 'react';
import type React from 'react';

export const useRovingTabIndex = (containerRef: React.RefObject<HTMLElement | null>): void => {
  const updateTabIndexes = useCallback(() => {
    const container: HTMLElement | null = containerRef.current;
    if (!container) return;

    const items = Array.from(container.querySelectorAll<HTMLElement>('[data-roving="item"]:not([disabled])'));
    if (items.length === 0) return;

    const activeElement = document.activeElement as HTMLElement;

    let activeIndex = items.indexOf(activeElement);
    if (activeIndex === -1) activeIndex = 0;

    items.forEach((item, index) => {
      item.tabIndex = index === activeIndex ? 0 : -1;
      item.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
    });

    return { items, activeIndex };
  }, [containerRef]);

  useEffect(() => {
    const container: HTMLElement | null = containerRef.current;
    if (!container) return;

    const result = updateTabIndexes();
    if (!result) return;

    function handleKey(event: KeyboardEvent): void {
      if (!container) return;
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;

      event.preventDefault();

      const items = Array.from(container.querySelectorAll<HTMLElement>('[data-roving="item"]:not([disabled])'));

      if (items.length === 0) return;

      const activeElement = document.activeElement as HTMLElement;

      let activeIndex = items.indexOf(activeElement);

      if (activeIndex === -1) activeIndex = 0;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        activeIndex = (activeIndex + 1) % items.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        activeIndex = (activeIndex - 1 + items.length) % items.length;
      }

      items.forEach((item, index) => {
        item.tabIndex = index === activeIndex ? 0 : -1;
        item.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
      });

      const targetItem = items[activeIndex];
      if (targetItem && targetItem.focus) {
        targetItem.focus();
      }
    }

    container.addEventListener('keydown', handleKey);

    const observer = new MutationObserver(updateTabIndexes);
    observer.observe(container, { childList: true, subtree: true });

    return (): void => {
      container.removeEventListener('keydown', handleKey);
      observer.disconnect();
    };
  }, [containerRef, updateTabIndexes]);
};
